'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Archive,
  ArrowRight,
  BadgeCheck,
  BookOpenText,
  Building2,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Eye,
  FileCheck2,
  FolderOpen,
  HeartHandshake,
  Home,
  Landmark,
  Lightbulb,
  Link2,
  LockKeyhole,
  Mic2,
  Music2,
  PenLine,
  Play,
  Save,
  ShieldCheck,
  Users,
} from 'lucide-react';
import LandingNav from '@/app/public-landing-page/components/LandingNav';
import AppLogo from '@/components/ui/AppLogo';
import MilestoneJourney from './MilestoneJourney';
import { useTranslation } from '@/i18n/useTranslation';

/** Shared button treatments so every CTA in the lab -- hero, record journey, record types,
 * nostalgia calculator, final CTA -- shares one height/padding/hierarchy recipe instead of
 * each section inventing its own. */
const BTN_PRIMARY =
  'inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#c39a5b] px-6 py-3 text-center text-sm font-bold text-[#10211c] transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-[#dfbd7a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1614] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100';
const BTN_SECONDARY =
  'inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[#d8c49a]/[0.42] bg-[#071310]/[0.3] px-6 py-3 text-center text-sm font-bold text-[#f1e8d6] transition-[background-color,border-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:border-[#efd49b]/[0.72] hover:bg-[#14251f]/[0.76] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1614] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100';
const CHIP_MONO = 'font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#d2b477]';

/** Hex values for each record type's accent identity, used for the persistent left-edge
 * identity bar in the record type list (previously the `accent` field was unused). */
const RECORD_ACCENT_HEX: Record<string, string> = {
  coral: '#e88a73',
  amber: '#d9a441',
  teal: '#1d9e75',
};

const trustIndicators = [
  { label: 'Personal records', tKey: 'memory_lab.trust_personal_records' },
  { label: 'Nostalgia records', tKey: 'memory_lab.trust_nostalgia_records' },
  { label: 'Optional witnesses', tKey: 'memory_lab.trust_optional_witnesses' },
  { label: 'Institutional proof', tKey: 'memory_lab.trust_institutional_proof' },
  { label: 'Public or private vaults', tKey: 'memory_lab.trust_public_private_vaults' },
];

const livingRecordStages = [
  {
    title: 'Memory',
    state: 'Saved',
    description: 'Capture the moment before it disappears.',
    example: 'Year, place, photo, sound, or object.',
    action: 'Add memory detail',
    sentence: 'Capture what happened, who was there, and why it still matters.',
    additions: 'Year, place, media, objects, people, and a private reflection.',
    access: 'Private by default',
    icon: Camera,
    panel: 'border-[#e88a73]/[0.28] bg-[#2b1d1a]/[0.52]',
    iconClass: 'bg-[#3a211c] text-[#ffb09c] border-[#e88a73]/[0.35]',
  },
  {
    title: 'Story',
    state: 'Structured',
    description: 'Turn the memory into a record with context.',
    example: 'Dates, people, media, and ownership.',
    action: 'Build story record',
    sentence: 'Add dates, context, media, people, and record ownership.',
    additions: 'Dates, people, media, context, visibility, and record ownership.',
    access: 'Private or public',
    icon: BookOpenText,
    panel: 'border-[#1d9e75]/[0.30] bg-[#10251f]/[0.56]',
    iconClass: 'bg-[#12372c] text-[#98f2d3] border-[#1d9e75]/[0.40]',
  },
  {
    title: 'Witness',
    state: 'Supported',
    description: 'Invite someone connected to the moment.',
    example: 'Family member, teammate, teacher, colleague.',
    action: 'Invite witness',
    sentence: 'Invite someone connected to the moment to add context.',
    additions: 'A witness, their relationship to the record, and their supporting context.',
    access: 'Witness supported',
    icon: Users,
    panel: 'border-[#e88a73]/[0.26] bg-[#2b1d1a]/[0.48]',
    iconClass: 'bg-[#3a211c] text-[#ffb09c] border-[#e88a73]/[0.32]',
  },
  {
    title: 'Verification',
    state: 'Trusted',
    description: 'Attach proof when a record needs public credibility.',
    example: 'School, club, employer, institution.',
    action: 'Request verification',
    sentence: 'Attach institutional proof for credentials and achievements.',
    additions: 'An institution, proof source, credential, membership, or achievement detail.',
    access: 'Institution verified',
    icon: ShieldCheck,
    panel: 'border-[#5fa8d3]/[0.28] bg-[#132431]/[0.56]',
    iconClass: 'bg-[#162e40] text-[#a9d9f2] border-[#5fa8d3]/[0.36]',
  },
  {
    title: 'Legacy',
    state: 'Preserved',
    description: 'Keep it private, publish it, or preserve it for the future.',
    example: 'Public vault, private archive, family record.',
    action: 'Preserve record',
    sentence: 'Keep it private, publish it, or preserve it for the future.',
    additions: 'Visibility, audience, preservation settings, and future access.',
    access: 'Private, public, or verified',
    icon: Archive,
    panel: 'border-[#d9a441]/[0.26] bg-[#2a2418]/[0.50]',
    iconClass: 'bg-[#3b2d13] text-[#f4d48a] border-[#d9a441]/[0.32]',
  },
];

const ledgerViews = {
  preservation: {
    title: 'Preservation ledger',
    description:
      'For individuals, families, creators, and communities preserving memories, stories, milestones, and personal history.',
    icon: Archive,
    panel: 'border-[#e88a73]/[0.34] bg-[#2b1d1a]/[0.70]',
    iconClass: 'bg-[#3a211c] text-[#ffb09c]',
    connector: 'border-[#e88a73]/[0.30] bg-[#3a211c]/[0.36]',
    items: [
      'Private vaults',
      'Public storytelling',
      'Family memories',
      'Nostalgia records',
      'Life milestones',
    ],
  },
  verification: {
    title: 'Verification ledger',
    description:
      'For schools, organizations, clubs, employers, and trusted groups verifying achievements, memberships, credentials, and records.',
    icon: ShieldCheck,
    panel: 'border-[#5fa8d3]/[0.34] bg-[#132431]/[0.72]',
    iconClass: 'bg-[#162e40] text-[#a9d9f2]',
    connector: 'border-[#5fa8d3]/[0.34] bg-[#162e40]/[0.4]',
    items: [
      'Verified achievements',
      'Membership records',
      'Credential proof',
      'Witness context',
      'Public trust pages',
    ],
  },
} as const;

const recordTypes = [
  {
    label: 'Life story',
    description: 'Preserve a meaningful chapter with dates, people, places, media, and context.',
    bestFor: 'Personal history, identity, and important turning points.',
    credibility: 'Private or witness supported.',
    example: 'A first-generation graduate preserving the chapter of leaving home for university.',
    button: 'Create this record',
    icon: BookOpenText,
    accent: 'coral',
    className: 'border-[#e88a73]/[0.30] bg-[#2b1d1a]/[0.66]',
    iconClass: 'text-[#ffb09c] bg-[#3a211c]',
  },
  {
    label: 'Achievement',
    description: 'Save a milestone, award, credential, or completed goal.',
    bestFor: 'Education, work, sport, service, and recognition.',
    credibility: 'Witness supported or institution verified.',
    example: 'A certification exam pass, with the issuing body attached as a witness.',
    button: 'Save achievement',
    icon: BadgeCheck,
    accent: 'amber',
    className: 'border-[#d9a441]/[0.30] bg-[#2a2418]/[0.66]',
    iconClass: 'text-[#f4d48a] bg-[#3b2d13]',
  },
  {
    label: 'Performance',
    description: 'Archive a recital, match, talk, exhibition, or public effort.',
    bestFor: 'Creative work, sport, speaking, and public contribution.',
    credibility: 'Witness supported.',
    example: 'A closing-night recital, with the program and a teacher as witness.',
    button: 'Archive performance',
    icon: Mic2,
    accent: 'teal',
    className: 'border-[#1d9e75]/[0.30] bg-[#10251f]/[0.68]',
    iconClass: 'text-[#98f2d3] bg-[#12372c]',
  },
  {
    label: 'Family memory',
    description: 'Keep details that family members remember from different angles.',
    bestFor: 'Family history, heritage, and shared memory.',
    credibility: 'Witness supported.',
    example: "A grandparent's migration story, corroborated by two siblings.",
    button: 'Preserve family memory',
    icon: Home,
    accent: 'coral',
    className: 'border-[#e88a73]/[0.26] bg-[#2b1d1a]/[0.60]',
    iconClass: 'text-[#ffb09c] bg-[#3a211c]',
  },
  {
    label: 'Favorite thing',
    description: 'Record an object, song, place, photograph, or keepsake that carries meaning.',
    bestFor: 'Nostalgia, identity, and personal archive.',
    credibility: 'Private record.',
    example: 'The song that played at a first apartment, kept private with a short note.',
    button: 'Save favorite',
    icon: HeartHandshake,
    accent: 'teal',
    className: 'border-[#1d9e75]/[0.26] bg-[#10251f]/[0.62]',
    iconClass: 'text-[#98f2d3] bg-[#12372c]',
  },
  {
    label: 'Civic contribution',
    description: 'Document service, leadership, volunteer work, and local impact with dignity.',
    bestFor: 'Community work, public service, and contribution.',
    credibility: 'Witness supported or institution verified.',
    example: 'A decade of food-bank volunteering, verified by the organization.',
    button: 'Document contribution',
    icon: Landmark,
    accent: 'teal',
    className: 'border-[#1d9e75]/[0.28] bg-[#10251f]/[0.64]',
    iconClass: 'text-[#98f2d3] bg-[#12372c]',
  },
];

const memoryCards = [
  {
    title: 'Sound',
    icon: Music2,
    iconClass: 'border-[#e88a73]/[0.30] bg-[#3a211c] text-[#ffb09c]',
  },
  {
    title: 'Place',
    icon: Home,
    iconClass: 'border-[#1d9e75]/[0.32] bg-[#12372c] text-[#98f2d3]',
  },
  {
    title: 'People',
    icon: Users,
    iconClass: 'border-[#1d9e75]/[0.32] bg-[#12372c] text-[#98f2d3]',
  },
  {
    title: 'Object',
    icon: Camera,
    iconClass: 'border-[#d9a441]/[0.30] bg-[#3b2d13] text-[#f4d48a]',
  },
  {
    title: 'Moment',
    icon: CalendarDays,
    iconClass: 'border-[#1d9e75]/[0.32] bg-[#12372c] text-[#98f2d3]',
  },
  {
    title: 'Witness',
    icon: PenLine,
    iconClass: 'border-[#1d9e75]/[0.32] bg-[#12372c] text-[#98f2d3]',
  },
];

const credibilityLevels = [
  {
    title: 'Private record',
    copy: 'Only the record owner can see it.',
    visibility: 'Owner only',
    trust: 'Ownership and record date',
    contributors: 'The record creator',
    bestUse: 'Personal memory, sensitive history, and work in progress',
    icon: LockKeyhole,
    className: 'border-[#1d9e75]/[0.32] bg-[#10251f]/[0.72]',
    iconClass: 'border-[#1d9e75]/[0.34] bg-[#12372c] text-[#98f2d3]',
  },
  {
    title: 'Witness supported',
    copy: 'Invite someone connected to the memory or achievement.',
    visibility: 'Owner controlled',
    trust: 'Witness supported',
    contributors: 'Invited people connected to the record',
    bestUse: 'Shared memories, performances, and personal achievements',
    icon: Users,
    className: 'border-[#e88a73]/[0.32] bg-[#2b1d1a]/[0.72]',
    iconClass: 'border-[#e88a73]/[0.34] bg-[#3a211c] text-[#ffb09c]',
  },
  {
    title: 'Institution verified',
    copy: 'Attach trusted proof from an organization when available.',
    visibility: 'Owner controlled or public',
    trust: 'Institution verified',
    contributors: 'A trusted organization and the record owner',
    bestUse: 'Credentials, memberships, and public achievements',
    icon: Building2,
    className: 'border-[#5fa8d3]/[0.32] bg-[#132431]/[0.72]',
    iconClass: 'border-[#5fa8d3]/[0.34] bg-[#162e40] text-[#a9d9f2]',
  },
];

type LedgerMode = keyof typeof ledgerViews;

const LIVING_STAGE_TKEYS: Record<
  string,
  {
    title: string;
    state: string;
    description: string;
    example: string;
    action: string;
    sentence: string;
    additions: string;
    access: string;
  }
> = {
  Memory: {
    title: 'memory_lab.stage_memory_title',
    state: 'memory_lab.stage_memory_state',
    description: 'memory_lab.stage_memory_description',
    example: 'memory_lab.stage_memory_example',
    action: 'memory_lab.stage_memory_action',
    sentence: 'memory_lab.stage_memory_sentence',
    additions: 'memory_lab.stage_memory_additions',
    access: 'memory_lab.stage_memory_access',
  },
  Story: {
    title: 'memory_lab.stage_story_title',
    state: 'memory_lab.stage_story_state',
    description: 'memory_lab.stage_story_description',
    example: 'memory_lab.stage_story_example',
    action: 'memory_lab.stage_story_action',
    sentence: 'memory_lab.stage_story_sentence',
    additions: 'memory_lab.stage_story_additions',
    access: 'memory_lab.stage_story_access',
  },
  Witness: {
    title: 'memory_lab.stage_witness_title',
    state: 'memory_lab.stage_witness_state',
    description: 'memory_lab.stage_witness_description',
    example: 'memory_lab.stage_witness_example',
    action: 'memory_lab.stage_witness_action',
    sentence: 'memory_lab.stage_witness_sentence',
    additions: 'memory_lab.stage_witness_additions',
    access: 'memory_lab.stage_witness_access',
  },
  Verification: {
    title: 'memory_lab.stage_verification_title',
    state: 'memory_lab.stage_verification_state',
    description: 'memory_lab.stage_verification_description',
    example: 'memory_lab.stage_verification_example',
    action: 'memory_lab.stage_verification_action',
    sentence: 'memory_lab.stage_verification_sentence',
    additions: 'memory_lab.stage_verification_additions',
    access: 'memory_lab.stage_verification_access',
  },
  Legacy: {
    title: 'memory_lab.stage_legacy_title',
    state: 'memory_lab.stage_legacy_state',
    description: 'memory_lab.stage_legacy_description',
    example: 'memory_lab.stage_legacy_example',
    action: 'memory_lab.stage_legacy_action',
    sentence: 'memory_lab.stage_legacy_sentence',
    additions: 'memory_lab.stage_legacy_additions',
    access: 'memory_lab.stage_legacy_access',
  },
};

const CREDIBILITY_TKEYS: Record<
  string,
  {
    title: string;
    copy: string;
    visibility: string;
    trust: string;
    contributors: string;
    bestUse: string;
  }
> = {
  'Private record': {
    title: 'memory_lab.cred_private_title',
    copy: 'memory_lab.cred_private_copy',
    visibility: 'memory_lab.cred_private_visibility',
    trust: 'memory_lab.cred_private_trust',
    contributors: 'memory_lab.cred_private_contributors',
    bestUse: 'memory_lab.cred_private_best_use',
  },
  'Witness supported': {
    title: 'memory_lab.cred_witness_title',
    copy: 'memory_lab.cred_witness_copy',
    visibility: 'memory_lab.cred_witness_visibility',
    trust: 'memory_lab.cred_witness_trust',
    contributors: 'memory_lab.cred_witness_contributors',
    bestUse: 'memory_lab.cred_witness_best_use',
  },
  'Institution verified': {
    title: 'memory_lab.cred_institution_title',
    copy: 'memory_lab.cred_institution_copy',
    visibility: 'memory_lab.cred_institution_visibility',
    trust: 'memory_lab.cred_institution_trust',
    contributors: 'memory_lab.cred_institution_contributors',
    bestUse: 'memory_lab.cred_institution_best_use',
  },
};

const RECORD_TYPE_TKEYS: Record<
  string,
  {
    label: string;
    desc: string;
    bestFor: string;
    credibility: string;
    example: string;
    button: string;
  }
> = {
  'Life story': {
    label: 'memory_lab.rt_life_story_label',
    desc: 'memory_lab.rt_life_story_desc',
    bestFor: 'memory_lab.rt_life_story_best_for',
    credibility: 'memory_lab.rt_life_story_credibility',
    example: 'memory_lab.rt_life_story_example',
    button: 'memory_lab.rt_life_story_button',
  },
  Achievement: {
    label: 'memory_lab.rt_achievement_label',
    desc: 'memory_lab.rt_achievement_desc',
    bestFor: 'memory_lab.rt_achievement_best_for',
    credibility: 'memory_lab.rt_achievement_credibility',
    example: 'memory_lab.rt_achievement_example',
    button: 'memory_lab.rt_achievement_button',
  },
  Performance: {
    label: 'memory_lab.rt_performance_label',
    desc: 'memory_lab.rt_performance_desc',
    bestFor: 'memory_lab.rt_performance_best_for',
    credibility: 'memory_lab.rt_performance_credibility',
    example: 'memory_lab.rt_performance_example',
    button: 'memory_lab.rt_performance_button',
  },
  'Family memory': {
    label: 'memory_lab.rt_family_memory_label',
    desc: 'memory_lab.rt_family_memory_desc',
    bestFor: 'memory_lab.rt_family_memory_best_for',
    credibility: 'memory_lab.rt_family_memory_credibility',
    example: 'memory_lab.rt_family_memory_example',
    button: 'memory_lab.rt_family_memory_button',
  },
  'Favorite thing': {
    label: 'memory_lab.rt_favorite_thing_label',
    desc: 'memory_lab.rt_favorite_thing_desc',
    bestFor: 'memory_lab.rt_favorite_thing_best_for',
    credibility: 'memory_lab.rt_favorite_thing_credibility',
    example: 'memory_lab.rt_favorite_thing_example',
    button: 'memory_lab.rt_favorite_thing_button',
  },
  'Civic contribution': {
    label: 'memory_lab.rt_civic_contribution_label',
    desc: 'memory_lab.rt_civic_contribution_desc',
    bestFor: 'memory_lab.rt_civic_contribution_best_for',
    credibility: 'memory_lab.rt_civic_contribution_credibility',
    example: 'memory_lab.rt_civic_contribution_example',
    button: 'memory_lab.rt_civic_contribution_button',
  },
};

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function centerInParent(id: string, focus = false) {
  const node = document.getElementById(id);
  if (!node) return;
  if (focus) node.focus({ preventScroll: true });
  node.scrollIntoView({
    block: 'nearest',
    inline: 'center',
    behavior: focus || prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

function focusAndCenterInParent(id: string) {
  centerInParent(id, true);
}

function SectionHeader({
  title,
  description,
  align = 'left',
  folio,
}: {
  title: string;
  description: string;
  align?: 'left' | 'center';
  folio?: string;
}) {
  return (
    <div
      className={`${align === 'center' ? 'mx-auto text-center' : ''} min-w-0 max-w-full sm:max-w-3xl`}
    >
      {folio && (
        <p
          aria-hidden="true"
          className="mb-5 font-mono text-[11px] uppercase tracking-[0.22em] text-[#b99458]"
        >
          {folio}
        </p>
      )}
      <h2
        className="break-words text-balance text-4xl leading-[1.05] text-[#f4efe4] sm:text-5xl lg:text-[3.45rem]"
        style={{ fontFamily: 'DM Serif Display, serif' }}
      >
        {title}
      </h2>
      <p className="mt-5 max-w-2xl break-words text-base leading-8 text-[#bfc9c0] sm:text-lg">
        {description}
      </p>
    </div>
  );
}

function LabPanel({
  children,
  className = '',
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={`relative w-full min-w-0 max-w-full ${className}`}>
      {children}
    </section>
  );
}

function HeroPreview() {
  const { t } = useTranslation();
  const [activeStage, setActiveStage] = useState(0);
  const stage = livingRecordStages[activeStage];
  const stageT = LIVING_STAGE_TKEYS[stage.title];

  function moveStage(index: number) {
    const nextIndex = (index + livingRecordStages.length) % livingRecordStages.length;
    setActiveStage(nextIndex);
    focusAndCenterInParent(`hero-stage-${nextIndex}`);
  }

  function handleStageKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveStage(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveStage(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      moveStage(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      moveStage(livingRecordStages.length - 1);
    }
  }

  return (
    <div className="w-full border-y border-[#d8c49a]/[0.28] bg-[#07120f]/[0.74] backdrop-blur-[2px]">
      <div className="grid min-w-0 lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)]">
        <div
          role="tablist"
          aria-label={t('memory_lab.hero_stages_aria' as any)}
          className="scrollbar-none flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain lg:grid lg:grid-cols-5 lg:overflow-visible"
        >
          {livingRecordStages.map((item, index) => {
            const isActive = index === activeStage;
            const itemT = LIVING_STAGE_TKEYS[item.title];

            return (
              <button
                key={item.title}
                id={`hero-stage-${index}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="hero-record-stage-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => {
                  setActiveStage(index);
                  centerInParent(`hero-stage-${index}`);
                }}
                onKeyDown={(event) => handleStageKeyDown(event, index)}
                className={`group min-w-[132px] snap-start border-r border-[#f1e8d6]/[0.1] px-4 py-5 text-left transition-[background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#efd49b] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 lg:min-w-0 ${
                  isActive ? 'bg-[#d8c49a]/[0.12]' : 'hover:bg-[#f1e8d6]/[0.06]'
                }`}
              >
                <span className="block font-mono text-[10px] tracking-[0.16em] text-[#b99458]">
                  0{index + 1}
                </span>
                <span
                  className={`mt-2 block text-sm font-semibold ${isActive ? 'text-[#fff7e8]' : 'text-[#9eaaa1]'}`}
                >
                  {t(itemT?.title as any) || item.title}
                </span>
              </button>
            );
          })}
        </div>

        <div
          id="hero-record-stage-panel"
          role="tabpanel"
          aria-labelledby={`hero-stage-${activeStage}`}
          className="flex min-h-[132px] items-center border-t border-[#f1e8d6]/[0.1] px-5 py-5 lg:border-l lg:border-t-0 lg:px-7"
        >
          <div key={stage.title} className="w-full motion-safe:animate-fade-in">
            <div className="flex items-center justify-between gap-4">
              <span className={CHIP_MONO}>{t(stageT?.state as any) || stage.state}</span>
              <span className="font-mono text-[10px] text-[#8d9a91]">
                {t('memory_lab.stage_count_of' as any, {
                  current: String(activeStage + 1),
                  total: String(livingRecordStages.length),
                })}
              </span>
            </div>
            <p className="mt-3 max-w-md text-sm leading-6 text-[#e3ded2]">
              {t(stageT?.description as any) || stage.description}
            </p>
            {activeStage === livingRecordStages.length - 1 ? (
              <Link
                href="/register"
                className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#dfbd7a] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b]"
              >
                {t(stageT?.action as any) || stage.action}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => moveStage(activeStage + 1)}
                className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#dfbd7a] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b]"
              >
                {t(stageT?.action as any) || stage.action}
                <ArrowRight size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const { t } = useTranslation();
  return (
    <section className="relative isolate flex min-h-[880px] w-full min-w-0 max-w-full flex-col justify-end overflow-hidden px-4 pb-10 pt-28 sm:px-6 sm:pb-12 lg:min-h-[920px] lg:px-8 lg:pb-14 lg:pt-36">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <Image
          src="https://images.unsplash.com/photo-1554279411-440148135a2b?auto=format&fit=crop&w=2400&q=80"
          alt=""
          fill
          priority
          sizes="100vw"
          className="-scale-x-100 object-cover object-[61%_76%] sm:object-[62%_72%]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg,rgba(5,15,12,0.94) 0%,rgba(5,15,12,0.82) 38%,rgba(5,15,12,0.28) 72%,rgba(5,15,12,0.58) 100%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg,rgba(7,18,15,0.18) 0%,rgba(7,18,15,0.05) 42%,rgba(7,18,15,0.62) 82%,#07120f 100%)',
          }}
        />
        <div className="absolute inset-0 bg-[#07120f]/[0.18] sm:hidden" />
      </div>

      <div className="mx-auto w-full min-w-0 max-w-[90rem]">
        <div className="grid min-w-0 gap-12 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,0.28fr)] lg:items-end">
          <div className="min-w-0 pb-2 lg:pb-8">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-[#d2b477]">
              Ertzyx Memory Lab
            </p>
            <h1
              className="mt-6 max-w-5xl break-words text-balance text-5xl leading-[0.98] text-[#f7f0e2] sm:text-6xl lg:text-[5.5rem] xl:text-[6.4rem]"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              Your life is not a feed.{' '}
              <span className="text-[#d2b477]">It is a living record.</span>
            </h1>
            <p className="mt-7 max-w-xl break-words text-lg leading-8 text-[#d4d5cc] sm:text-xl">
              {t('memory_lab.subtitle' as any)}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link href="/register" className={`${BTN_PRIMARY} w-full sm:w-auto`}>
                {t('memory_lab.cta_start_vault' as any)}
                <LockKeyhole size={16} aria-hidden="true" />
              </Link>
              <Link href="#verification-ledger" className={`${BTN_SECONDARY} w-full sm:w-auto`}>
                {t('memory_lab.cta_explore_verification' as any)}
                <ShieldCheck size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="hidden border-l border-[#d8c49a]/[0.28] pb-8 pl-7 lg:block">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#b99458]">
              A record can hold
            </p>
            <ul className="mt-5 space-y-3">
              {trustIndicators.map((item, index) => (
                <li key={item.label} className="flex items-baseline gap-3 text-sm text-[#d7dbd2]">
                  <span className="font-mono text-[10px] text-[#8b998f]">0{index + 1}</span>
                  {t(item.tKey as any) || item.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <HeroPreview />
          </div>
        </div>
      </div>
    </section>
  );
}

function LivingRecordFlow() {
  const { t } = useTranslation();
  const [activeStage, setActiveStage] = useState(0);
  const stage = livingRecordStages[activeStage];
  const stageT = LIVING_STAGE_TKEYS[stage.title];
  const StageIcon = stage.icon;

  function selectStage(index: number, focus = false) {
    const nextIndex = (index + livingRecordStages.length) % livingRecordStages.length;
    setActiveStage(nextIndex);
    if (focus) focusAndCenterInParent(`living-stage-${nextIndex}`);
    else centerInParent(`living-stage-${nextIndex}`);
  }

  function handleStageKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const keyActions: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: livingRecordStages.length - 1,
    };
    const nextIndex = keyActions[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    selectStage(nextIndex, true);
  }

  return (
    <LabPanel className="border-t border-[#d8c49a]/[0.2] py-20 sm:py-24 lg:py-32">
      <div className="grid min-w-0 gap-12 lg:grid-cols-[minmax(280px,0.68fr)_minmax(0,1.32fr)] lg:gap-20">
        <SectionHeader
          folio="02 — Anatomy of a record"
          title={t('memory_lab.living_flow_title' as any)}
          description={t('memory_lab.living_flow_desc' as any)}
        />

        <div
          role="tablist"
          aria-label={t('memory_lab.living_stages_aria' as any)}
          className="scrollbar-none flex snap-x snap-mandatory overflow-x-auto border-t border-[#f1e8d6]/[0.14] overscroll-x-contain lg:block lg:overflow-visible"
        >
          {livingRecordStages.map((item, index) => {
            const isActive = activeStage === index;
            const itemT = LIVING_STAGE_TKEYS[item.title];

            return (
              <button
                key={item.title}
                id={`living-stage-${index}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="living-record-panel"
                aria-expanded={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => selectStage(index)}
                onKeyDown={(event) => handleStageKeyDown(event, index)}
                className={`group flex min-w-[190px] snap-start items-center gap-5 border-b border-[#f1e8d6]/[0.14] px-2 py-4 text-left transition-[background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#efd49b] active:scale-[0.985] motion-reduce:transition-none motion-reduce:active:scale-100 lg:w-full ${
                  isActive ? 'bg-[#f1e8d6]/[0.07]' : 'hover:bg-[#f1e8d6]/[0.035]'
                }`}
              >
                <span
                  className={`font-mono text-xs ${isActive ? 'text-[#dfbd7a]' : 'text-[#6f7f75]'}`}
                >
                  0{index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-base font-bold ${isActive ? 'text-[#f7f0e2]' : 'text-[#a9b2aa]'}`}
                  >
                    {t(itemT?.title as any) || item.title}
                  </span>
                  <span className="mt-0.5 block text-xs text-[#7d8c83]">
                    {t(itemT?.state as any) || item.state}
                  </span>
                </span>
                <ArrowRight
                  size={15}
                  className={`shrink-0 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none ${isActive ? 'translate-x-0 text-[#dfbd7a]' : '-translate-x-1 text-[#596961]'}`}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      </div>

      <div
        id="living-record-panel"
        role="tabpanel"
        aria-labelledby={`living-stage-${activeStage}`}
        className="mt-12 w-full min-w-0 border-y border-[#d8c49a]/[0.24] bg-[#111d18] py-8 sm:px-8 sm:py-10 lg:px-12"
      >
        <div
          key={stage.title}
          className="grid min-w-0 gap-10 motion-safe:animate-fade-in lg:grid-cols-[minmax(240px,0.72fr)_minmax(0,1.28fr)] lg:gap-16"
        >
          <div className="min-w-0 px-5 sm:px-0">
            <div className="flex items-center justify-between gap-4">
              <span
                className={`flex h-11 w-11 items-center justify-center rounded-full border ${stage.iconClass}`}
              >
                <StageIcon size={19} aria-hidden="true" />
              </span>
              <span className={CHIP_MONO}>{t(stageT?.state as any) || stage.state}</span>
            </div>
            <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8d9a91]">
              {t('memory_lab.active_stage_label' as any)}
            </p>
            <h3
              className="mt-3 break-words text-4xl text-[#f4efe4] sm:text-5xl"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {t(stageT?.title as any) || stage.title}
            </h3>
            <p className="mt-5 break-words text-base leading-8 text-[#cbd1c9]">
              {t(stageT?.sentence as any) || stage.sentence}
            </p>
          </div>

          <dl className="grid min-w-0 border-t border-[#f1e8d6]/[0.14] sm:grid-cols-2">
            {[
              {
                label: t('memory_lab.what_stage_does' as any),
                value: t(stageT?.description as any) || stage.description,
                icon: CircleDot,
              },
              {
                label: t('memory_lab.what_you_can_add' as any),
                value: t(stageT?.additions as any) || stage.additions,
                icon: FolderOpen,
              },
              {
                label: t('memory_lab.trust_privacy_label' as any),
                value: t(stageT?.access as any) || stage.access,
                icon: ShieldCheck,
              },
              {
                label: t('memory_lab.record_state_label' as any),
                value: t(stageT?.state as any) || stage.state,
                icon: Eye,
              },
            ].map((detail, index) => {
              const DetailIcon = detail.icon;
              return (
                <div
                  key={detail.label}
                  className={`min-w-0 border-b border-[#f1e8d6]/[0.14] px-5 py-6 sm:px-6 ${index % 2 === 1 ? 'sm:border-l' : ''}`}
                >
                  <dt className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#a99b7d]">
                    <DetailIcon size={14} aria-hidden="true" />
                    {detail.label}
                  </dt>
                  <dd className="mt-3 break-words text-sm font-semibold leading-6 text-[#e4e0d7]">
                    {detail.value}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </LabPanel>
  );
}

const LEDGER_TKEYS: Record<LedgerMode, { title: string; description: string; items: string[] }> = {
  preservation: {
    title: 'memory_lab.ledger_preservation_title',
    description: 'memory_lab.ledger_preservation_desc',
    items: [
      'memory_lab.ledger_preservation_item1',
      'memory_lab.ledger_preservation_item2',
      'memory_lab.ledger_preservation_item3',
      'memory_lab.ledger_preservation_item4',
      'memory_lab.ledger_preservation_item5',
    ],
  },
  verification: {
    title: 'memory_lab.ledger_verification_title',
    description: 'memory_lab.ledger_verification_desc',
    items: [
      'memory_lab.ledger_verification_item1',
      'memory_lab.ledger_verification_item2',
      'memory_lab.ledger_verification_item3',
      'memory_lab.ledger_verification_item4',
      'memory_lab.ledger_verification_item5',
    ],
  },
};

function TwoLedgers() {
  const { t } = useTranslation();
  const [ledgerMode, setLedgerMode] = useState<LedgerMode>('preservation');
  const activeLedger = ledgerViews[ledgerMode];
  const ActiveIcon = activeLedger.icon;
  const modes = Object.keys(ledgerViews) as LedgerMode[];

  function handleLedgerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % modes.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + modes.length) % modes.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = modes.length - 1;
    setLedgerMode(modes[nextIndex]);
    focusAndCenterInParent(`ledger-tab-${modes[nextIndex]}`);
  }

  return (
    <LabPanel
      id="verification-ledger"
      className="scroll-mt-28 overflow-hidden border-y border-[#c5ad7b]/[0.5] bg-[#e8dfcc] px-5 py-20 text-[#15241f] sm:px-8 sm:py-24 lg:px-12 lg:py-32"
    >
      <div className="grid min-w-0 gap-12 lg:grid-cols-[minmax(300px,0.72fr)_minmax(0,1.28fr)] lg:gap-20">
        <div className="min-w-0">
          <p
            aria-hidden="true"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#886b3d]"
          >
            03 — Preservation & proof
          </p>
          <h2
            className="mt-5 max-w-2xl text-balance text-4xl leading-[1.02] text-[#15241f] sm:text-5xl lg:text-[3.5rem]"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            {t('memory_lab.two_ledgers_title' as any)}
          </h2>
          <p className="mt-6 max-w-xl text-base leading-8 text-[#4d5a53] sm:text-lg">
            {t('memory_lab.two_ledgers_desc' as any)}
          </p>

          <div
            role="tablist"
            aria-label={t('memory_lab.ledger_views_aria' as any)}
            className="mt-10 border-t border-[#233d33]/[0.24]"
          >
            {modes.map((mode, index) => {
              const ledger = ledgerViews[mode];
              const ledgerT = LEDGER_TKEYS[mode];
              const Icon = ledger.icon;
              const selected = ledgerMode === mode;

              return (
                <button
                  key={mode}
                  id={`ledger-tab-${mode}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="ledger-detail-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setLedgerMode(mode)}
                  onKeyDown={(event) => handleLedgerKeyDown(event, index)}
                  className={`group flex w-full min-w-0 items-center gap-4 border-b border-[#233d33]/[0.24] px-1 py-5 text-left transition-[background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#6f532d] active:scale-[0.985] motion-reduce:transition-none motion-reduce:active:scale-100 ${selected ? 'bg-[#fffaf0]/[0.55]' : 'hover:bg-[#fffaf0]/[0.3]'}`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${selected ? 'bg-[#183d32] text-[#f2e8d4]' : 'border border-[#233d33]/[0.24] text-[#49645a]'}`}
                  >
                    <Icon size={17} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-lg font-bold text-[#182a23]">
                      {t(ledgerT.title as any) || ledger.title}
                    </span>
                    <span className="mt-1 hidden break-words text-sm leading-6 text-[#58655e] sm:block">
                      {t(ledgerT.description as any) || ledger.description}
                    </span>
                  </span>
                  <ArrowRight
                    size={16}
                    className={selected ? 'text-[#886b3d]' : 'text-[#8c958f]'}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>

          <div className="mt-8 flex items-start gap-3 border-l-2 border-[#a2773f] pl-5">
            <Link2 size={17} className="mt-1 shrink-0 text-[#76592f]" aria-hidden="true" />
            <p className="max-w-md text-sm font-semibold leading-7 text-[#35483f]">
              {t('memory_lab.ledger_connector_sentence' as any)}
            </p>
          </div>
        </div>

        <div
          id="ledger-detail-panel"
          role="tabpanel"
          aria-labelledby={`ledger-tab-${ledgerMode}`}
          className="relative w-full min-w-0 border-l border-[#233d33]/[0.24] pl-6 sm:pl-10 lg:pl-14"
        >
          <div key={ledgerMode} className="relative motion-safe:animate-fade-in">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#183d32] text-[#f2e8d4]">
              <ActiveIcon size={22} aria-hidden="true" />
            </span>
            <h3
              className="mt-8 break-words text-4xl text-[#15241f] sm:text-5xl"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {t(LEDGER_TKEYS[ledgerMode].title as any) || activeLedger.title}
            </h3>
            <p className="mt-5 max-w-2xl break-words text-base leading-8 text-[#4d5a53]">
              {t(LEDGER_TKEYS[ledgerMode].description as any) || activeLedger.description}
            </p>
            <p className="mt-10 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#886b3d]">
              {t('memory_lab.example_records_label' as any)}
            </p>
            <div className="mt-4 grid border-t border-[#233d33]/[0.2] sm:grid-cols-2">
              {activeLedger.items.map((item, itemIndex) => (
                <div
                  key={item}
                  className={`flex min-w-0 items-center gap-3 border-b border-[#233d33]/[0.2] py-4 text-sm font-semibold text-[#263a31] sm:px-4 ${itemIndex % 2 === 1 ? 'sm:border-l' : ''}`}
                >
                  <CheckCircle2 size={15} className="shrink-0 text-[#6f532d]" aria-hidden="true" />
                  <span className="min-w-0 break-words">
                    {t(LEDGER_TKEYS[ledgerMode].items[itemIndex] as any) || item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LabPanel>
  );
}

function RecordTypeSelector() {
  const { t } = useTranslation();
  const [selectedRecord, setSelectedRecord] = useState(recordTypes[0].label);
  const selected = useMemo(
    () => recordTypes.find((record) => record.label === selectedRecord) ?? recordTypes[0],
    [selectedRecord]
  );
  const selectedT = RECORD_TYPE_TKEYS[selected.label];
  const SelectedIcon = selected.icon;

  return (
    <LabPanel id="record-types" className="border-t border-[#d8c49a]/[0.2] py-20 sm:py-24 lg:py-32">
      <div className="mb-12 grid gap-7 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,0.28fr)] lg:items-end">
        <SectionHeader
          folio="04 — Record explorer"
          title={t('memory_lab.record_type_selector_title' as any)}
          description={t('memory_lab.record_type_selector_desc' as any)}
        />
        <p className="max-w-sm border-l border-[#d8c49a]/[0.3] pl-5 text-sm leading-7 text-[#9ea9a0] lg:justify-self-end">
          Choose the shape that best holds the moment. The record can evolve as context, witness,
          and proof are added.
        </p>
      </div>

      <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(260px,0.62fr)_minmax(0,1.38fr)] lg:gap-16">
        <div
          role="group"
          aria-label={t('memory_lab.record_types_aria' as any)}
          className="scrollbar-none -mx-4 flex w-auto min-w-0 snap-x snap-mandatory overflow-x-auto overscroll-x-contain border-y border-[#f1e8d6]/[0.16] px-4 lg:mx-0 lg:block lg:w-full lg:overflow-visible lg:border-b-0 lg:px-0"
        >
          {recordTypes.map((record, index) => {
            const Icon = record.icon;
            const isSelected = record.label === selectedRecord;
            const recordT = RECORD_TYPE_TKEYS[record.label];
            const accentHex = RECORD_ACCENT_HEX[record.accent];

            return (
              <button
                key={record.label}
                type="button"
                aria-pressed={isSelected}
                aria-controls="selected-record-panel"
                onClick={() => setSelectedRecord(record.label)}
                style={{
                  borderLeftColor: isSelected ? accentHex : `${accentHex}4d`,
                  borderLeftWidth: isSelected ? 3 : 0,
                }}
                className={`group flex min-h-[82px] w-[220px] min-w-[220px] snap-start items-center gap-3 border-r border-[#f1e8d6]/[0.16] px-3 py-4 text-left transition-[background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#efd49b] active:scale-[0.985] motion-reduce:transition-none motion-reduce:active:scale-100 lg:w-full lg:min-w-0 lg:border-b lg:border-r-0 ${isSelected ? 'bg-[#f1e8d6]/[0.07]' : 'hover:bg-[#f1e8d6]/[0.035]'}`}
              >
                <span className="font-mono text-[10px] text-[#7b8a81]">0{index + 1}</span>
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${isSelected ? record.iconClass : 'border-[#f1e8d6]/[0.16] text-[#829188]'}`}
                >
                  <Icon size={16} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block whitespace-nowrap text-[15px] font-bold ${isSelected ? 'text-[#f7f0e2]' : 'text-[#aeb7af]'}`}
                  >
                    {t(recordT.label as any) || record.label}
                  </span>
                </span>
                <ChevronRight
                  size={15}
                  className={isSelected ? 'text-[#dfbd7a]' : 'text-[#5f6e65]'}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        <article
          id="selected-record-panel"
          aria-live="polite"
          className="relative min-h-[520px] w-full min-w-0 overflow-hidden rounded-[2px] bg-[#e9dfca] px-6 py-8 text-[#17261f] shadow-[0_24px_70px_rgba(0,0,0,0.2)] sm:px-10 sm:py-11 lg:px-14"
        >
          <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-[#a86f43]" />
          <div key={selected.label} className="relative motion-safe:animate-fade-in">
            <div className="flex items-start justify-between gap-4 border-b border-[#263b32]/[0.22] pb-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#263b32]/[0.25] text-[#6f532d]">
                <SelectedIcon size={21} aria-hidden="true" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#795f36]">
                {t('memory_lab.selected_record_label' as any)}
              </span>
            </div>

            <h3
              className="mt-8 break-words text-4xl leading-tight text-[#17261f] sm:text-5xl"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {t(selectedT.label as any) || selected.label}
            </h3>
            <p className="mt-5 max-w-2xl break-words text-base leading-8 text-[#46564e]">
              {t(selectedT.desc as any) || selected.description}
            </p>

            <dl className="mt-9 grid min-w-0 border-y border-[#263b32]/[0.2] sm:grid-cols-2">
              <div className="min-w-0 py-5 sm:pr-6">
                <dt className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#795f36]">
                  <CalendarDays size={14} aria-hidden="true" />
                  {t('memory_lab.best_for_label' as any)}
                </dt>
                <dd className="mt-3 break-words text-sm font-semibold leading-6 text-[#263b32]">
                  {t(selectedT.bestFor as any) || selected.bestFor}
                </dd>
              </div>
              <div className="min-w-0 border-t border-[#263b32]/[0.2] py-5 sm:border-l sm:border-t-0 sm:pl-6">
                <dt className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#795f36]">
                  <ShieldCheck size={14} aria-hidden="true" />
                  {t('memory_lab.suggested_credibility_label' as any)}
                </dt>
                <dd className="mt-3 break-words text-sm font-semibold leading-6 text-[#263b32]">
                  {t(selectedT.credibility as any) || selected.credibility}
                </dd>
              </div>
            </dl>

            <div className="mt-8 border-l-2 border-[#a86f43] pl-5">
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#795f36]">
                <Lightbulb size={14} aria-hidden="true" />
                {t('memory_lab.rt_example_use_label' as any)}
              </p>
              <p className="mt-3 break-words text-sm italic leading-7 text-[#35483f]">
                {t(selectedT.example as any) || selected.example}
              </p>
            </div>

            <Link href="/register" className={`${BTN_PRIMARY} mt-9 w-full max-w-full sm:w-auto`}>
              {t(selectedT.button as any) || selected.button}
              <ChevronRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </article>
      </div>
    </LabPanel>
  );
}

const MEMORY_CARD_TITLE_KEYS: Record<string, string> = {
  Sound: 'memory_lab.card_sound',
  Place: 'memory_lab.card_place',
  People: 'memory_lab.card_people',
  Object: 'memory_lab.card_object',
  Moment: 'memory_lab.card_moment',
  Witness: 'memory_lab.card_witness',
};

const MEMORY_CARD_DESC_KEYS: Record<string, string> = {
  Sound: 'memory_lab.card_sound_desc',
  Place: 'memory_lab.card_place_desc',
  People: 'memory_lab.card_people_desc',
  Object: 'memory_lab.card_object_desc',
  Moment: 'memory_lab.card_moment_desc',
  Witness: 'memory_lab.card_witness_desc',
};

function NostalgiaCalculator() {
  const { t } = useTranslation();
  const [year, setYear] = useState('1995');
  const [revealedYear, setRevealedYear] = useState<number | null>(null);
  const [activeMemory, setActiveMemory] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [savedCards, setSavedCards] = useState<Record<string, boolean>>({});
  const [witnessInvites, setWitnessInvites] = useState<Record<string, boolean>>({});
  const currentYear = new Date().getFullYear();
  const selectedCard = memoryCards[activeMemory];
  const SelectedCardIcon = selectedCard.icon;
  const selectedCardKey = revealedYear ? `${revealedYear}-${selectedCard.title}` : '';

  function memoryCardCopy(title: string) {
    const copy: Record<string, string> = {
      Sound: 'The song, voice, or background sound that brings the year back.',
      Place: 'The room, street, school, city, or home you still remember.',
      People: 'The people who shaped that season of your life.',
      Object: 'The photo, letter, toy, device, clothing, or keepsake worth preserving.',
      Moment: 'The event, habit, trip, celebration, or quiet detail that still matters.',
      Witness: 'Someone who could add context, memory, or confirmation.',
    };
    return copy[title];
  }

  function eraLabel(selectedYear: number) {
    if (selectedYear < 1920) return t('memory_lab.era_early_20th' as any);
    if (selectedYear < 1945) return t('memory_lab.era_between_wars' as any);
    if (selectedYear < 1960) return t('memory_lab.era_postwar' as any);
    if (selectedYear < 1970) return t('memory_lab.era_1960s' as any);
    if (selectedYear < 1980) return t('memory_lab.era_1970s' as any);
    if (selectedYear < 1990) return t('memory_lab.era_1980s' as any);
    if (selectedYear < 2000) return t('memory_lab.era_1990s' as any);
    if (selectedYear < 2010) return t('memory_lab.era_2000s' as any);
    if (selectedYear < 2020) return t('memory_lab.era_2010s' as any);
    return t('memory_lab.era_2020s' as any);
  }

  function handleReveal(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsedYear = Number(year);
    if (!/^\d{4}$/.test(year) || parsedYear < 1900 || parsedYear > currentYear) {
      setError(t('memory_lab.year_error' as any, { year: String(currentYear) }));
      return;
    }
    setError(null);
    setRevealedYear(parsedYear);
    setActiveMemory(0);
  }

  return (
    <LabPanel className="border-y border-[#bd875d]/[0.26] bg-[#1c1510] px-5 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-32">
      <div className="grid min-w-0 gap-14 lg:grid-cols-[minmax(300px,0.72fr)_minmax(0,1.28fr)] lg:items-start lg:gap-20">
        <div className="w-full min-w-0 max-w-xl">
          <SectionHeader
            folio="05 — Memory capsule"
            title={t('memory_lab.nostalgia_calculator_title' as any)}
            description={t('memory_lab.nostalgia_calculator_desc' as any)}
          />
          <form
            onSubmit={handleReveal}
            className="mt-10 w-full min-w-0 max-w-full border-t border-[#f1e8d6]/[0.16] pt-7"
          >
            <label
              htmlFor="memory-year"
              className="block font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#d3a477]"
            >
              {t('memory_lab.year_question' as any)}
            </label>
            <div className="mt-4 flex min-w-0 items-end gap-4 border-b border-[#f1e8d6]/[0.24] pb-4">
              <input
                id="memory-year"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={year}
                onChange={(event) => {
                  setYear(event.target.value.replace(/[^\d]/g, '').slice(0, 4));
                  setError(null);
                }}
                placeholder="1995"
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'memory-year-hint memory-year-error' : 'memory-year-hint'}
                className="min-h-16 w-full min-w-0 bg-transparent font-mono text-5xl font-medium leading-none text-[#f4efe4] outline-none placeholder:text-[#6f6257] focus-visible:ring-2 focus-visible:ring-[#efd49b] sm:text-6xl"
              />
              <span className="pb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8e8175]">
                Year
              </span>
            </div>

            <div className="mt-7">
              <input
                id="memory-year-range"
                type="range"
                min={1900}
                max={currentYear}
                step={1}
                value={Number(year) >= 1900 && Number(year) <= currentYear ? Number(year) : 1995}
                onChange={(event) => {
                  setYear(event.target.value);
                  setError(null);
                }}
                aria-label="Choose a memory year"
                className="h-2 w-full cursor-ew-resize accent-[#c39a5b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b]"
              />
              <div className="mt-2 flex justify-between font-mono text-[10px] text-[#796e64]">
                <span>1900</span>
                <span>{currentYear}</span>
              </div>
            </div>

            <p
              id="memory-year-hint"
              className="mt-5 text-xs font-semibold leading-6 text-[#aeb4aa]"
            >
              {t('memory_lab.year_hint' as any, { year: String(currentYear) })}
            </p>
            {error && (
              <p
                id="memory-year-error"
                role="alert"
                className="mt-2 text-sm font-bold text-[#ffb09c]"
              >
                {error}
              </p>
            )}

            <button type="submit" className={`${BTN_PRIMARY} mt-6 w-full max-w-full sm:w-auto`}>
              {t('memory_lab.reveal_capsule' as any)}
              <Play size={15} fill="currentColor" aria-hidden="true" />
            </button>
          </form>
        </div>

        <div className="w-full min-w-0 max-w-full lg:border-l lg:border-[#f1e8d6]/[0.14] lg:pl-10 xl:pl-16">
          {revealedYear ? (
            <article
              aria-live="polite"
              className="relative rounded-[2px] bg-[#e9dfca] px-5 py-7 text-[#17261f] shadow-[0_28px_80px_rgba(0,0,0,0.28)] motion-safe:animate-fade-in sm:px-8 sm:py-9"
            >
              <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-[#a86f43]" />
              <div className="flex flex-wrap items-start justify-between gap-5 border-b border-[#263b32]/[0.22] pb-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#795f36]">
                    Archive drawer / {eraLabel(revealedYear)}
                  </p>
                  <h3 className="mt-2 font-mono text-5xl font-medium leading-none text-[#17261f] sm:text-6xl">
                    {revealedYear}
                  </h3>
                </div>
                <Link
                  href="/register"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-[#263b32]/[0.28] px-4 py-2 text-xs font-bold text-[#263b32] transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-[#fffaf0]/[0.65] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6f532d] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
                >
                  {t('memory_lab.preserve_this_year' as any)}
                  <Archive size={14} aria-hidden="true" />
                </Link>
              </div>

              <div className="mt-6 flex items-center gap-2 text-xs font-semibold text-[#486057]">
                <LockKeyhole size={13} aria-hidden="true" />
                {t('memory_lab.private_by_default' as any)}
              </div>

              <div
                role="tablist"
                aria-label={t('memory_lab.memory_cards' as any)}
                className="mt-7 grid grid-cols-2 border-t border-[#263b32]/[0.2] sm:grid-cols-3"
              >
                {memoryCards.map((card, index) => {
                  const Icon = card.icon;
                  const isActive = activeMemory === index;
                  return (
                    <button
                      key={card.title}
                      id={`memory-card-tab-${index}`}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls="memory-card-detail"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActiveMemory(index)}
                      onKeyDown={(event) => {
                        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                        event.preventDefault();
                        let nextIndex = index;
                        if (event.key === 'ArrowRight') {
                          nextIndex = (index + 1) % memoryCards.length;
                        }
                        if (event.key === 'ArrowLeft') {
                          nextIndex = (index - 1 + memoryCards.length) % memoryCards.length;
                        }
                        if (event.key === 'Home') nextIndex = 0;
                        if (event.key === 'End') nextIndex = memoryCards.length - 1;
                        setActiveMemory(nextIndex);
                        focusAndCenterInParent(`memory-card-tab-${nextIndex}`);
                      }}
                      className={`flex min-h-14 items-center gap-2 border-b border-[#263b32]/[0.2] px-2 py-3 text-left text-sm font-bold transition-[background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#6f532d] active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 ${index % 2 === 1 ? 'border-l sm:border-l-0' : ''} ${index % 3 !== 0 ? 'sm:border-l' : ''} ${isActive ? 'bg-[#fffaf0]/[0.72] text-[#17261f]' : 'text-[#66746d] hover:bg-[#fffaf0]/[0.36]'}`}
                    >
                      <Icon
                        size={15}
                        className={isActive ? 'text-[#795f36]' : 'text-[#7c8882]'}
                        aria-hidden="true"
                      />
                      {t(MEMORY_CARD_TITLE_KEYS[card.title] as any) || card.title}
                    </button>
                  );
                })}
              </div>

              <div
                id="memory-card-detail"
                role="tabpanel"
                aria-labelledby={`memory-card-tab-${activeMemory}`}
                className="mt-8 w-full min-w-0 max-w-full"
              >
                <div key={selectedCard.title} className="motion-safe:animate-fade-in">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#263b32]/[0.24] text-[#795f36]">
                      <SelectedCardIcon size={18} aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="break-words text-lg font-bold text-[#17261f]">
                        {t(MEMORY_CARD_TITLE_KEYS[selectedCard.title] as any) || selectedCard.title}{' '}
                        {t('memory_lab.reflection_suffix' as any)}
                      </p>
                      <p className="mt-1 max-w-xl text-sm leading-6 text-[#56655e]">
                        {t(MEMORY_CARD_DESC_KEYS[selectedCard.title] as any) ||
                          memoryCardCopy(selectedCard.title)}
                      </p>
                    </div>
                  </div>

                  <label
                    htmlFor="private-memory-note"
                    className="mt-7 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#795f36]"
                  >
                    {t('memory_lab.add_private_reflection' as any)}
                  </label>
                  <textarea
                    id="private-memory-note"
                    rows={3}
                    value={notes[selectedCardKey] ?? ''}
                    onChange={(event) =>
                      setNotes((current) => ({
                        ...current,
                        [selectedCardKey]: event.target.value,
                      }))
                    }
                    placeholder={t('memory_lab.reflection_placeholder' as any, {
                      year: String(revealedYear),
                    })}
                    className="mt-3 w-full min-w-0 max-w-full resize-y rounded-[2px] border border-[#263b32]/[0.24] bg-[#fffaf0]/[0.55] px-4 py-3 text-sm leading-6 text-[#17261f] outline-none transition-[border-color,box-shadow] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] placeholder:text-[#7a857f] focus:border-[#6f532d] focus:shadow-[0_0_0_3px_rgba(111,83,45,0.14)]"
                  />

                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      aria-pressed={Boolean(savedCards[selectedCardKey])}
                      onClick={() =>
                        setSavedCards((current) => ({
                          ...current,
                          [selectedCardKey]: !current[selectedCardKey],
                        }))
                      }
                      className="inline-flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-full border border-[#263b32]/[0.28] px-4 py-2.5 text-center text-sm font-bold text-[#27483b] transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-[#fffaf0]/[0.7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#315c4c] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
                    >
                      <Save size={16} aria-hidden="true" />
                      {savedCards[selectedCardKey]
                        ? t('memory_lab.saved_for_later' as any)
                        : t('memory_lab.save_for_later' as any)}
                    </button>
                    <button
                      type="button"
                      aria-pressed={Boolean(witnessInvites[selectedCardKey])}
                      onClick={() =>
                        setWitnessInvites((current) => ({
                          ...current,
                          [selectedCardKey]: !current[selectedCardKey],
                        }))
                      }
                      className="inline-flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-full border border-[#a86f43]/[0.42] px-4 py-2.5 text-center text-sm font-bold text-[#7f4d30] transition-[background-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-[#fffaf0]/[0.7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a86f43] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
                    >
                      <Users size={16} aria-hidden="true" />
                      {witnessInvites[selectedCardKey]
                        ? t('memory_lab.witness_invite_noted' as any)
                        : t('memory_lab.invite_a_witness' as any)}
                    </button>
                  </div>
                  <p className="mt-4 text-xs leading-5 text-[#65716b]">
                    {t('memory_lab.reflection_note_hint' as any)}
                  </p>
                </div>
              </div>
            </article>
          ) : (
            <div className="relative mx-auto max-w-xl px-3 py-8 sm:px-8">
              <div className="relative aspect-[4/3] overflow-hidden rounded-[2px] border border-[#c89a72]/[0.36] bg-[#d9c9ad] p-8 text-[#26382f] shadow-[0_28px_70px_rgba(0,0,0,0.3)] sm:p-10">
                <div
                  aria-hidden="true"
                  className="absolute inset-x-[12%] top-[17%] h-px origin-center rotate-[24deg] bg-[#826c51]/[0.3]"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-x-[12%] top-[17%] h-px origin-center -rotate-[24deg] bg-[#826c51]/[0.3]"
                />
                <Archive size={25} className="text-[#805436]" aria-hidden="true" />
                <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.18em] text-[#795f36]">
                  {t('memory_lab.memory_cards' as any)} / sealed
                </p>
                <p
                  className="mt-3 max-w-sm text-3xl leading-tight text-[#1c2d25] sm:text-4xl"
                  style={{ fontFamily: 'DM Serif Display, serif' }}
                >
                  {t('memory_lab.year_can_hold_more' as any)}
                </p>
              </div>
              <p className="mx-auto mt-7 max-w-md text-center text-sm leading-7 text-[#b8b4a9]">
                {t('memory_lab.capsule_closing' as any)}
              </p>
            </div>
          )}
        </div>
      </div>
    </LabPanel>
  );
}

function CredibilityLayer() {
  const { t } = useTranslation();
  const [activeLevel, setActiveLevel] = useState(0);
  const level = credibilityLevels[activeLevel];
  const levelT = CREDIBILITY_TKEYS[level.title];
  const LevelIcon = level.icon;

  return (
    <LabPanel className="border-t border-[#d8c49a]/[0.2] py-20 sm:py-24 lg:py-32">
      <div className="grid min-w-0 gap-12 lg:grid-cols-[minmax(300px,0.72fr)_minmax(0,1.28fr)] lg:gap-20">
        <div>
          <SectionHeader
            folio="06 — Credibility, by choice"
            title={t('memory_lab.credibility_title' as any)}
            description={t('memory_lab.credibility_desc' as any)}
          />

          <div
            role="tablist"
            aria-label={t('memory_lab.credibility_levels_aria' as any)}
            className="scrollbar-none -mx-4 mt-10 flex w-auto min-w-0 snap-x snap-mandatory overflow-x-auto overscroll-x-contain border-y border-[#f1e8d6]/[0.16] px-4 lg:mx-0 lg:block lg:w-full lg:overflow-visible lg:border-b-0 lg:px-0"
          >
            {credibilityLevels.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeLevel === index;
              const itemT = CREDIBILITY_TKEYS[item.title];

              return (
                <button
                  key={item.title}
                  id={`credibility-tab-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="credibility-detail-panel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveLevel(index)}
                  onKeyDown={(event) => {
                    if (
                      !['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(
                        event.key
                      )
                    )
                      return;
                    event.preventDefault();
                    let nextIndex = index;
                    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                      nextIndex = (index + 1) % credibilityLevels.length;
                    }
                    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                      nextIndex = (index - 1 + credibilityLevels.length) % credibilityLevels.length;
                    }
                    if (event.key === 'Home') nextIndex = 0;
                    if (event.key === 'End') nextIndex = credibilityLevels.length - 1;
                    setActiveLevel(nextIndex);
                    focusAndCenterInParent(`credibility-tab-${nextIndex}`);
                  }}
                  className={`relative flex w-[220px] min-w-[220px] snap-start items-center gap-3 border-r border-[#f1e8d6]/[0.16] px-3 py-5 text-left transition-[background-color,color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#efd49b] active:scale-[0.985] motion-reduce:transition-none motion-reduce:active:scale-100 lg:w-full lg:min-w-0 lg:border-b lg:border-r-0 ${isActive ? 'bg-[#f1e8d6]/[0.07]' : 'hover:bg-[#f1e8d6]/[0.035]'}`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${isActive ? item.iconClass : 'border-[#f1e8d6]/[0.16] text-[#7f8e85]'}`}
                  >
                    <Icon size={17} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-[10px] uppercase tracking-[0.14em] text-[#7f8e85]">
                      {t('memory_lab.level_label' as any, { number: String(index + 1) })}
                    </span>
                    <span
                      className={`mt-1 block break-words text-lg font-bold ${isActive ? 'text-[#f7f0e2]' : 'text-[#aeb7af]'}`}
                    >
                      {t(itemT.title as any) || item.title}
                    </span>
                  </span>
                  <ArrowRight
                    size={15}
                    className={isActive ? 'text-[#dfbd7a]' : 'text-[#5f6e65]'}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div
          id="credibility-detail-panel"
          role="tabpanel"
          aria-labelledby={`credibility-tab-${activeLevel}`}
          className="relative w-full min-w-0 max-w-full border-y border-[#d8c49a]/[0.24] bg-[#111d18] px-5 py-8 sm:px-8 sm:py-10 lg:px-12"
        >
          <div key={level.title} className="relative motion-safe:animate-fade-in">
            <span
              className={`flex h-14 w-14 items-center justify-center rounded-full border ${level.iconClass}`}
            >
              <LevelIcon size={23} aria-hidden="true" />
            </span>
            <h3
              className="mt-8 break-words text-4xl text-[#f4efe4] sm:text-5xl"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              {t(levelT.title as any) || level.title}
            </h3>
            <p className="mt-4 max-w-xl break-words text-base leading-8 text-[#c9d3ca]">
              {t(levelT.copy as any) || level.copy}
            </p>

            <dl className="mt-8 grid min-w-0 grid-cols-[minmax(0,1fr)] border-t border-[#f1e8d6]/[0.14] sm:grid-cols-2">
              {[
                {
                  label: t('memory_lab.visibility_label' as any),
                  value: t(levelT.visibility as any) || level.visibility,
                  icon: Eye,
                },
                {
                  label: t('memory_lab.trust_signal_label' as any),
                  value: t(levelT.trust as any) || level.trust,
                  icon: BadgeCheck,
                },
                {
                  label: t('memory_lab.who_can_contribute_label' as any),
                  value: t(levelT.contributors as any) || level.contributors,
                  icon: Users,
                },
                {
                  label: t('memory_lab.best_use_case_label' as any),
                  value: t(levelT.bestUse as any) || level.bestUse,
                  icon: FileCheck2,
                },
              ].map((detail, index) => {
                const DetailIcon = detail.icon;
                return (
                  <div
                    key={detail.label}
                    className={`min-w-0 border-b border-[#f1e8d6]/[0.14] py-5 sm:px-5 ${index % 2 === 1 ? 'sm:border-l' : ''}`}
                  >
                    <dt className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-[#a99b7d]">
                      <DetailIcon size={14} className="text-[#d2b477]" aria-hidden="true" />
                      {detail.label}
                    </dt>
                    <dd className="mt-2 break-words text-sm font-semibold leading-6 text-[#e4e8df]">
                      {detail.value}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </div>

      <p className="mt-10 max-w-4xl break-words border-l-2 border-[#b99458] pl-5 text-sm font-semibold leading-7 text-[#d7fff2]">
        {t('memory_lab.credibility_footer_note' as any)}
      </p>
    </LabPanel>
  );
}

function FinalCta() {
  const { t } = useTranslation();
  return (
    <section className="relative w-full min-w-0 max-w-full border-y border-[#d8c49a]/[0.24] py-16 sm:py-20 lg:py-24">
      <div className="relative grid min-w-0 grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#b99458]">
            Begin with one moment
          </p>
          <h2
            className="mt-5 max-w-4xl break-words text-balance text-4xl leading-[1.02] text-[#f4efe4] sm:text-5xl lg:text-6xl"
            style={{ fontFamily: 'DM Serif Display, serif' }}
          >
            {t('memory_lab.final_cta_title' as any)}
          </h2>
          <p className="mt-5 max-w-2xl break-words text-base leading-8 text-[#c9d3ca] sm:text-lg">
            {t('memory_lab.final_cta_subtitle' as any)}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-stretch">
          <Link href="/register" className={`${BTN_PRIMARY} w-full max-w-full sm:w-auto`}>
            {t('memory_lab.cta_start_vault' as any)}
            <LockKeyhole size={16} aria-hidden="true" />
          </Link>
          <Link href="/trust-ledger" className={`${BTN_SECONDARY} w-full sm:w-auto`}>
            {t('memory_lab.final_cta_view_trust_ledger' as any)}
            <ShieldCheck size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function MemoryLabFooter() {
  const { t } = useTranslation();
  const footerLinks = [
    { label: 'Product', href: '/product', tKey: 'nav.product' },
    { label: 'Preservation ledger', href: '/preservation-ledger', tKey: 'nav.preservation' },
    { label: 'Trust ledger', href: '/trust-ledger', tKey: 'nav.trust_ledger' },
    { label: 'Pricing', href: '/pricing', tKey: 'nav.pricing' },
  ];

  return (
    <footer className="relative w-full min-w-0 max-w-full overflow-x-hidden border-t border-[#d8c49a]/[0.2] bg-[#050d0b]">
      <div className="relative mx-auto w-full min-w-0 max-w-[90rem] px-5 py-12 sm:px-8 lg:px-12 lg:py-14">
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-10 border-b border-[#f1e8d6]/[0.10] pb-10 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div className="min-w-0 max-w-xl">
            <Link
              href="/"
              aria-label={t('nav.home_aria' as any)}
              className="inline-flex max-w-full min-w-0 items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050d0b]"
            >
              <AppLogo size={34} />
              <span className="min-w-0 break-words text-lg font-bold text-[#f4efe4]">
                {t('memory_lab.title' as any)}
              </span>
            </Link>
            <p className="mt-4 break-words text-sm leading-7 text-[#9db4aa]">
              {t('memory_lab.footer_tagline' as any)}
            </p>
          </div>

          <nav
            aria-label={t('memory_lab.footer_nav_aria' as any)}
            className="flex min-w-0 flex-wrap gap-x-5 gap-y-3"
          >
            {footerLinks.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="text-sm font-bold text-[#c9d3ca] underline-offset-4 transition-[color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:text-[#dfbd7a] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#efd49b] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
              >
                {t(item.tKey as any) || item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex flex-col gap-4 pt-6 text-xs font-semibold text-[#71847b] sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Yove Technologies Ltd.</p>
          <div className="flex gap-5">
            <Link
              href="/privacy-policy"
              className="rounded-md transition-colors duration-150 hover:text-[#c9d3ca] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#72dcb5]"
            >
              Privacy
            </Link>
            <Link
              href="/terms-of-service"
              className="rounded-md transition-colors duration-150 hover:text-[#c9d3ca] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#72dcb5]"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function InteractiveExperience() {
  return (
    <main
      data-theme="aurora"
      className="public-page public-family-memory min-h-screen w-full max-w-full overflow-x-hidden text-[#f4efe4]"
      style={{ backgroundColor: '#07120f' }}
    >
      <LandingNav />
      <div className="relative w-full min-w-0 max-w-full overflow-x-hidden">
        <HeroSection />

        <div className="relative mx-auto w-full min-w-0 max-w-[90rem] px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
          <div className="py-20 sm:py-24 lg:py-32">
            <MilestoneJourney />
          </div>
          <LivingRecordFlow />
          <TwoLedgers />
          <RecordTypeSelector />
          <NostalgiaCalculator />
          <CredibilityLayer />
          <FinalCta />
        </div>
      </div>
      <MemoryLabFooter />
    </main>
  );
}
